import { useCallback } from 'react';
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import {
  IconButton,
  List,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Link,
  Chip,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { get, isEmpty } from "lodash";
import { dateStringToDateTime } from "../../../utils/DateTime";

import ListItemButton from "@mui/material/ListItemButton";
import { makeStyles } from 'tss-react/mui';
const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "nowrap",
    alignItems: "center",
    marginTop: "0.2em",
    marginLeft: "0.2em",
    marginRight: "0.2em",
    border: "1px solid rgba(0, 0, 0, 0.05)",
  },
  list: {
    marginBottom: theme.spacing(2),
    "& .MuiListItem-gutters": {
      padding: "0 6px",
    },
    "& .MuiListItemAvatar-root": {
      minWidth: "50px",
    },
  },
  subheader: {
    backgroundColor: theme.palette.background.paper,
  },
  emptyNotifications: {
    marginTop: theme.spacing(3),
  },
  chip: {
    marginLeft: theme.spacing(1),
    height: "20px",
    fontSize: "0.6rem",
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    color: "#686767",
  },
  shown: {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
  },
  notShown: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
}));

const secondaryAsDiv = { secondary: { component: "div" } };

const ClearButton = ({ id, clearNotification }) => {
  const handleClick = useCallback(() => {
    clearNotification({ id: id });
  }, [id, clearNotification]);

  return (
    <IconButton onClick={handleClick} size="large">
      <CloseIcon />
    </IconButton>
  );
};

// CREW-1188 — opening a notification reads it, as ✕ does: before, only ✕
// did, and the badge counted everything nobody had cleared by hand. A click
// and the mouse wheel both open the task in a new tab; a right click does not.
const OpenLink = ({ id, clearNotification, ...props }) => {
  const read = useCallback(() => clearNotification({ id }), [id, clearNotification]);
  const readOnWheel = useCallback((e) => { if (e.button === 1) read(); }, [read]);

  return <Link {...props} onClick={read} onAuxClick={readOnWheel} />;
};

OpenLink.propTypes = {
  id: PropTypes.string.isRequired,
  clearNotification: PropTypes.func.isRequired,
};

const NotificationsList = ({ data, clearNotification }) => {
  const { classes } = useStyles();

  const chipClasses = useCallback(
    (isShown) => {
      return `${classes.chip} ${isShown ? classes.shown : classes.notShown }`;
    },
    [classes.chip, classes.shown, classes.notShown]
  );

  if (isEmpty(data)) {
    return (
      <>
        <Typography
          variant="h6"
          align="center"
          className={classes.emptyNotifications}
          gutterBottom
        >
          Notifications are empty
        </Typography>
      </>
    );
  }

  return (
    <>
      <List className={classes.list}>
        {data.map(({ id, attributes }) => (
          <div key={id}>
            <Paper className={classes.container}>
              <OpenLink
                id={id}
                clearNotification={clearNotification}
                component={RouterLink}
                to={get(attributes, "item_link")}
                underline="none"
                target="_blank"
                color="inherit"
              >
                <ListItemButton>
                  <ListItemAvatar>
                    <Avatar
                      alt="Profile Picture"
                      src={`/api/${get(attributes, "author_avatar")}`}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    // The date chip is a <div>; the default <p> cannot hold it.
                    slotProps={secondaryAsDiv}
                    primary={get(attributes, "primary_text")}
                    secondary={
                      <>
                        {get(attributes, "secondary_text")}
                        <Chip
                          className={chipClasses(get(attributes, "shown"))}
                          size="small"
                          label={dateStringToDateTime(
                            get(attributes, "created_at")
                          )}
                        />
                      </>
                    }
                  />
                </ListItemButton>
              </OpenLink>
              <ClearButton id={id} clearNotification={clearNotification} />
            </Paper>
          </div>
        ))}
      </List>
    </>
  );
};

NotificationsList.propTypes = {
  data: PropTypes.array,
  clearNotification: PropTypes.func.isRequired,
};
export default NotificationsList;
